/**
 * DataTablePagination Component
 *
 * Pagination footer with "Lines per page" InlineSelect selector and page navigation.
 */

import { memo, useMemo } from 'react';
import { InlineSelect } from '@/components/ui/inline-select';
import { cn } from '@/lib/utils';
import { ChevronIcon } from '../icons';
import {
  DEFAULT_PER_PAGE_OPTIONS,
  MAX_VISIBLE_PAGES,
  BORDER_RADIUS_CLASSES,
  FOOTER_CONTROL_SIZE_CLASSES,
} from './constants';

export interface DataTablePaginationProps {
  readonly currentPage: number;
  readonly totalPages: number;
  readonly perPage: number;
  readonly perPageOptions?: readonly number[];
  readonly canGoPrevious: boolean;
  readonly canGoNext: boolean;
  readonly onPageChange: (page: number) => void;
  readonly onPerPageChange?: (perPage: number) => void;
  readonly className?: string;
}

function getPageNumbers(currentPage: number, totalPages: number): (number | 'ellipsis')[] {
  if (totalPages <= MAX_VISIBLE_PAGES) {
    return Array.from({ length: totalPages }, (_, i) => i + 1);
  }

  const pages: (number | 'ellipsis')[] = [1];

  if (currentPage <= 3) {
    pages.push(2);
    if (totalPages > 3) pages.push(3);
    pages.push('ellipsis');
  } else if (currentPage >= totalPages - 2) {
    pages.push('ellipsis');
    if (totalPages > 3) pages.push(totalPages - 2);
    pages.push(totalPages - 1);
  } else {
    pages.push('ellipsis');
    pages.push(currentPage);
    pages.push('ellipsis');
  }

  pages.push(totalPages);

  return pages;
}

export const DataTablePagination = memo(function DataTablePagination({
  currentPage,
  totalPages,
  perPage,
  perPageOptions = DEFAULT_PER_PAGE_OPTIONS,
  canGoPrevious,
  canGoNext,
  onPageChange,
  onPerPageChange,
  className,
}: DataTablePaginationProps) {
  const pageNumbers = useMemo(
    () => getPageNumbers(currentPage, totalPages),
    [currentPage, totalPages]
  );

  return (
    <div
      className={cn(
        'flex-shrink-0',
        'flex items-center justify-between',
        className
      )}
    >
      <div className="flex items-center gap-4">
        <span className="font-inter text-nav leading-[1.5] text-grey">Lines per page</span>
        {onPerPageChange ? (
          <InlineSelect
            value={perPage}
            options={perPageOptions}
            onChange={(value) => {
              onPerPageChange(value);
              onPageChange(1);
            }}
          />
        ) : (
          <span className="font-inter text-nav font-semibold leading-[1.5] text-off-black">{perPage}</span>
        )}
      </div>

      {totalPages > 1 && (
        <nav
          role="navigation"
          aria-label="Pagination"
          className="inline-flex items-center gap-8"
        >
          <button
            onClick={() => canGoPrevious && onPageChange(currentPage - 1)}
            disabled={!canGoPrevious}
            aria-label="Go to previous page"
            className={cn(
              'flex items-center justify-center transition-colors',
              canGoPrevious ? 'text-off-black hover:text-grey cursor-pointer' : 'text-dinnerplate-white cursor-not-allowed'
            )}
          >
            <ChevronIcon direction="left" />
          </button>

          <div className="flex items-center gap-4">
            {pageNumbers.map((page, index) =>
              page === 'ellipsis' ? (
                <span
                  key={`ellipsis-${index}`}
                  aria-hidden="true"
                  className={cn(
                    FOOTER_CONTROL_SIZE_CLASSES,
                    'flex items-center justify-center font-semibold text-nav leading-[1.5] text-off-black'
                  )}
                >
                  ...
                </span>
              ) : (
                <button
                  key={page}
                  onClick={() => page !== currentPage && onPageChange(page)}
                  aria-label={`Go to page ${page}`}
                  aria-current={page === currentPage ? 'page' : undefined}
                  className={cn(
                    FOOTER_CONTROL_SIZE_CLASSES,
                    'p-2 flex items-center justify-center font-semibold text-nav leading-[1.5] transition-colors',
                    BORDER_RADIUS_CLASSES,
                    page === currentPage
                      ? 'bg-off-black text-dinnerplate-white'
                      : 'bg-off-white text-off-black hover:bg-dinnerplate-white-light'
                  )}
                >
                  {page}
                </button>
              )
            )}
          </div>

          <button
            onClick={() => canGoNext && onPageChange(currentPage + 1)}
            disabled={!canGoNext}
            aria-label="Go to next page"
            className={cn(
              'flex items-center justify-center transition-colors',
              canGoNext ? 'text-off-black hover:text-grey cursor-pointer' : 'text-dinnerplate-white cursor-not-allowed'
            )}
          >
            <ChevronIcon direction="right" />
          </button>
        </nav>
      )}
    </div>
  );
});
